/**
 * Selectable evaluation-list row used in the 30/70 split panes.
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState, type CSSProperties, type KeyboardEvent } from "react";

const DEFAULT_CARD_BG_COLOR = "#FFFFFF";
const DEFAULT_LEFT_BORDER_COLOR = "transparent";
const SELECTED_LEFT_BORDER_COLOR = "#0056D6";
const SELECTED_CARD_COLOR = "#EAF2FF";

/**
 * Props for a single evaluation criterion row.
 */
export interface EvaluationListCardProps {
    id: string;
    tagLabel: string;
    tagBackgroundColor: string;
    tagTextColor: string;
    positionText: string;
    criteriaDetails: string;
    cardBgColor?: string;
    leftBorderColor?: string;
    isSelected?: boolean;
    onClick?: (id: string) => void;
}

/**
 * Compact evaluation row with position, truncated criterion text, and a status tag.
 *
 * @param props - Criterion copy, tag styling, and card chrome
 * @param props.id - Unique id emitted to the consumer when the card is clicked
 * @param props.tagLabel - Status shown in the Oasis tag
 * @param props.tagBackgroundColor - Tag fill color
 * @param props.tagTextColor - Tag label color
 * @param props.positionText - Criterion index label (e.g. "#1")
 * @param props.criteriaDetails - Full criterion statement; truncated in the row
 * @param props.cardBgColor - Card background; defaults to white
 * @param props.leftBorderColor - 3px left-border color; defaults to transparent
 * @param props.isSelected - When true, uses #0056D6 for background and left border
 * @param props.onClick - Called with this card's id when the row is activated
 * @returns One evaluation criterion card
 */
const EvaluationListCard = ({
    id,
    tagLabel,
    tagBackgroundColor,
    tagTextColor,
    positionText,
    criteriaDetails,
    cardBgColor = DEFAULT_CARD_BG_COLOR,
    leftBorderColor = DEFAULT_LEFT_BORDER_COLOR,
    isSelected = false,
    onClick,
}: EvaluationListCardProps) => {
    const tagText = useMemo(() => {
        if (tagLabel.includes("Data Missing")) {
            return "Action Pending";
        }
        return tagLabel || "";
    }, [tagLabel]);
    const resolvedBgColor = isSelected ? SELECTED_CARD_COLOR : cardBgColor;
    const resolvedBorderColor = isSelected ? SELECTED_LEFT_BORDER_COLOR : leftBorderColor;
    const cardStyle = {
        "--evaluation-card-bg": resolvedBgColor,
        borderLeftColor: resolvedBorderColor,
    } as CSSProperties;

    /**
     * Emits this card's id to the parent when the row is clicked.
     */
    const handleClick = () => {
        onClick?.(id);
    };

    /**
     * Activates the card from the keyboard with Enter or Space.
     *
     * @param event - Keyboard event from the row
     */
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" && event.key !== " ") {
            return;
        }
        event.preventDefault();
        onClick?.(id);
    };

    return (
        <div
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            className={`single-evaluation-card--container flex gap-[0.6rem] items-center min-h-[3.75rem] overflow-hidden border-l-[3px] p-3 bg-[var(--evaluation-card-bg)] hover:cursor-pointer transition-all ${isSelected ? "" : "hover:bg-primaryGray-16"
                }`}
            style={cardStyle}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div className="evaluation-card-details flex-1 min-w-0 flex flex-col gap-2">
                <div
                    className="criteria-position text-overline font-semibold text-[#6B6B6B]"

                >
                    {positionText}
                </div>
                <div
                    className="criteria-details line-clamp-2 text-xs leading-[1.125rem] text-primaryGray-1"
                >
                    {criteriaDetails}
                </div>
            </div>
            <div className="met-not-met-details">
                <Tags
                    label={tagText}
                    backgroundColor={tagBackgroundColor}
                    textColor={tagTextColor}
                />
            </div>
        </div>
    );
};

export default EvaluationListCard;
